import React, { useMemo } from "react"
import style from "./ArticleParameters.module.scss"
import { ArticleEditFormValues, ArticleParameter, ArticleParameterValue } from "../../models/article"
import { nameof } from "../../utility/common/nameof"
import { FieldArray, useFormikContext } from "formik"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faGripLinesVertical, faTimes } from "@fortawesome/pro-light-svg-icons"
import { useAppSelector } from "../../store/hooks"
import { selectSlots } from "../../store/slots/selectors"
import { Form } from "react-bootstrap"
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd"
import cn from "classnames"
import DebouncedValidatableInput from "../ValidatableInput/DebouncedValidatableInput"
import { getSlotById, getSlotTitlesMap } from "../../utility/knowledgeBase/slot"

interface Props {
    parameters: ArticleParameter[]
}

const ArticleParameters: React.FC<Props> = props => {
    const { parameters } = props
    const slots = useAppSelector(selectSlots)

    return (
        <>
            {parameters.map((p, index) => {
                const slot = getSlotById(p.SlotId, slots)
                const slotIdComparison = p.SlotIdComparison?.toLowerCase()
                const slotValueComparison = p.ValueComparison?.toLowerCase()

                return (
                    <div key={index} className={style.articleParams__item}>
                        <span
                            className={cn(
                                slotIdComparison
                                    ? cn(style[`articleParams__title_${slotIdComparison}`], style.articleParams__title)
                                    : style.articleParams__title
                            )}
                        >
                            {slot?.Title}
                        </span>
                        <Form.Control
                            disabled
                            className={cn(
                                slotValueComparison
                                    ? cn(
                                          style[`articleParams__value_${slotValueComparison}`],
                                          style.articleParams__value
                                      )
                                    : style.articleParams__value
                            )}
                            value={p.Value}
                        ></Form.Control>
                    </div>
                )
            })}
        </>
    )
}

interface FormProps {
    addButton: React.ReactNode
}

const FormikArticleParameters: React.FC<FormProps> = ({ addButton }) => {
    const { values } = useFormikContext<ArticleEditFormValues>()
    const slots = useAppSelector(selectSlots)
    const slotsMap = useMemo(() => getSlotTitlesMap(slots), [slots])

    return (
        <FieldArray
            name={nameof<ArticleEditFormValues>("Parameters")}
            render={({ remove, move }) => (
                <>
                    <DragDropContext
                        onDragEnd={(result: DropResult) => {
                            if (!result.destination || result.destination.index === result.source.index) return
                            move(result.source.index, result.destination.index)
                        }}
                    >
                        <Droppable droppableId="droppable">
                            {provided => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className={style.articleParams}
                                >
                                    {values.Parameters.map((parameter: ArticleParameterValue, index: number) => (
                                        <Draggable key={parameter.Id} draggableId={parameter.Id} index={index}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    className={cn(
                                                        style.articleParams__item,
                                                        snapshot.isDragging && style.articleParams__item_dragging
                                                    )}
                                                >
                                                    <span
                                                        className={style.articleParams__dragIcon}
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <FontAwesomeIcon icon={faGripLinesVertical} />
                                                    </span>
                                                    <span className={style.articleParams__title}>
                                                        {slotsMap[parameter.SlotId]}
                                                    </span>
                                                    <DebouncedValidatableInput
                                                        id={`article-param${index}`}
                                                        className={style.articleParams__value}
                                                        name={`Parameters[${index}].Value`}
                                                    />
                                                    <FontAwesomeIcon
                                                        icon={faTimes}
                                                        className={style.articleParams__removeIcon}
                                                        onClick={() => remove(index)}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                    {values.Parameters.length !== 0 && addButton}
                </>
            )}
        />
    )
}

export { ArticleParameters, FormikArticleParameters }
